import React, { useMemo } from 'react';
import { Box, Card, Stack, Typography } from '@mui/material';
import {
  Business,
  Description,
  LibraryBooks,
  SvgIconComponent,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AccountStats } from '../../../hooks/api/useAccountStats';
import { isAccountExpired } from '../utils';

interface StatisticsCardsContainerProps {
  accountStats: AccountStats[];
  isLoading?: boolean;
}

interface CardData {
  icon: SvgIconComponent;
  iconColor: 'primary' | 'success' | 'info' | 'error' | 'warning';
  value: string | number;
  label: string;
  isLoading: boolean;
}

const StatisticsCard: React.FC<CardData> = ({
  icon: Icon,
  iconColor,
  value,
  label,
  isLoading = false,
}) => {
  return (
    <Card variant="primary" sx={{ height: '100%', p: 0 }}>
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          gap: 6,
        }}
      >
        <Box
          sx={{
            p: 1,
            display: 'flex',
            m: 10,
          }}
        >
          <Icon sx={{ color: `${iconColor}.main`, fontSize: 32 }} />
        </Box>
        <Typography variant="h4">
          {isLoading
            ? '-'
            : typeof value === 'number'
            ? value.toLocaleString()
            : value}
        </Typography>
        <Typography variant="body1">{label}</Typography>
      </Stack>
    </Card>
  );
};

export const StatisticsCardsContainer: React.FC<
  StatisticsCardsContainerProps
> = ({ accountStats, isLoading = false }) => {
  const { t } = useTranslation();

  const overallStats = useMemo(() => {
    const nonExpiredAccounts = accountStats.filter(
      (account) => !isAccountExpired(account.contract_expiration_date)
    );

    const stats = {
      activeAccounts: nonExpiredAccounts.length,
      totalAgreements: 0,
      totalJournals: 0,
    };

    nonExpiredAccounts.forEach((account) => {
      if (account.agreements?.active) {
        stats.totalAgreements += account.agreements.active.agreements || 0;
        stats.totalJournals += account.agreements.active.journals || 0;
      }
    });

    return stats;
  }, [accountStats]);

  const cards: CardData[] = [
    {
      icon: Business,
      iconColor: 'primary',
      value: overallStats.activeAccounts,
      label: t('accounts.activeAccounts'),
      isLoading,
    },
    {
      icon: Description,
      iconColor: 'success',
      value: overallStats.totalAgreements,
      label: t('accounts.totalAgreements'),
      isLoading,
    },
    {
      icon: LibraryBooks,
      iconColor: 'info',
      value: overallStats.totalJournals,
      label: t('accounts.totalJournals'),
      isLoading,
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 8,
      }}
    >
      {cards.map((card, index) => (
        <Box key={index} sx={{ flex: { md: 1 }, width: { xs: '100%' } }}>
          <StatisticsCard
            icon={card.icon}
            iconColor={card.iconColor}
            value={card.value}
            label={card.label}
            isLoading={card.isLoading}
          />
        </Box>
      ))}
    </Box>
  );
};
